/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Interface for activity log entries tracking user actions in the system.
 * Includes user info, action details, entity references, and metadata for ISO-27001 compliance.
 */
export interface ActivityLogInterface {
  id: string;
  userId: string;
  action: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  user?: {
    id: string;
    username: string;
  };
  person?: {
    name: string;
    lastname: string;
  };
}

/**
 * Data transfer object for querying activity logs with filters and pagination.
 */
export interface ActivityLogQueryDTO {
  page?: number;
  limit?: number;
  userId?: string;
  action?: string;
  entityType?: string;
  startDate?: string;
  endDate?: string;
  searchTerm?: string;
}

export interface ActivityLogResponseDTO {
  data: ActivityLogInterface[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Redux state for managing activity log data, pagination, and filters.
 */
export type ActivityLogState = {
  logs: ActivityLogInterface[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  filters: ActivityLogQueryDTO;
};

const initialState: ActivityLogState = {
  logs: [],
  total: 0,
  page: 1,
  limit: 50,
  totalPages: 0,
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 50,
  },
};

const ActivityLogSlice = createSlice({
  name: "activityLog",
  initialState: initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setLogs: (state, action: PayloadAction<ActivityLogResponseDTO>) => {
      state.logs = action.payload.data;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
    },
    /**
     * Updates activity log query filters by merging with existing filters.
     */
    setFilters: (state, action: PayloadAction<ActivityLogQueryDTO>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 50,
      };
    },
    clearLogs: (state) => {
      state.logs = [];
      state.total = 0;
      state.page = 1;
      state.totalPages = 0;
    },
  },
});

export const {
  setLoading,
  setError,
  setLogs,
  setFilters,
  resetFilters,
  clearLogs,
} = ActivityLogSlice.actions;

export const selectActivityLogs = (state: { activityLog: ActivityLogState }) => state.activityLog.logs;
export const selectActivityLogLoading = (state: { activityLog: ActivityLogState }) => state.activityLog.loading;
export const selectActivityLogError = (state: { activityLog: ActivityLogState }) => state.activityLog.error;
export const selectActivityLogFilters = (state: { activityLog: ActivityLogState }) => state.activityLog.filters;
export const selectActivityLogPagination = (state: { activityLog: ActivityLogState }) => ({
  page: state.activityLog.page,
  limit: state.activityLog.limit,
  total: state.activityLog.total,
  totalPages: state.activityLog.totalPages,
});

export default ActivityLogSlice;
